import { useTranslate } from "@tolgee/react";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Button } from "#/components/ui/button.tsx";
import { CheckboxIndicator } from "#/components/ui/checkbox.tsx";
import {
	Dialog,
	DialogCloseButton,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from "#/components/ui/dialog.tsx";
import { getHyperliquidErrorMessage } from "#/features/trade/lib/hyperliquid-error";
import { useUserAbstraction } from "#/hooks/use-hyperliquid-account";
import { useSetAccountAbstraction } from "#/hooks/use-trading-actions";
import { cn } from "#/lib/utils";

/** `agentSetAbstraction` codes: i = disabled (Manual), u = unifiedAccount, p = portfolioMargin. */
type AbstractionMode = "i" | "u" | "p";

const ACCOUNT_TYPE_OPTIONS: {
	value: AbstractionMode;
	labelKey: string;
	labelFallback: string;
	descKey: string;
	descFallback: string;
}[] = [
	{
		value: "u",
		labelKey: "account-type.unified.label",
		labelFallback: "Unified Account",
		descKey: "account-type.unified.desc",
		descFallback:
			"For advanced users who want greater flexibility and capital efficiency. All trading is unified across spot and perps. Eligible collateral assets such as HYPE and BTC can be used directly as collateral for perp positions without converting to the settlement asset. Collateral assets earn or pay interest depending on whether they are at rest or actively borrowed to open positions.",
	},
	{
		value: "p",
		labelKey: "account-type.portfolio-margin.label",
		labelFallback: "Portfolio Margin",
		descKey: "account-type.portfolio-margin.desc",
		descFallback:
			"Only recommended for automated traders. All DEXs have separate balances and cross margin applies separately within each DEX.",
	},
	{
		value: "i",
		labelKey: "account-type.manual.label",
		labelFallback: "Manual",
		descKey: "account-type.manual.desc",
		descFallback:
			"The default account setting where each collateral asset has a separate balance. Perps can only use the settlement asset as collateral, and margining is only shared across cross margin assets with the same collateral asset.",
	},
];

// Raw `userAbstraction` value → the dialog option it corresponds to.
// `dexAbstraction` (auto-enabled on the first xyz order) has no manual
// equivalent here → undefined (nothing preselected; any choice counts as a change).
const RAW_TO_VALUE: Record<string, AbstractionMode | undefined> = {
	default: "i",
	disabled: "i",
	unifiedAccount: "u",
	portfolioMargin: "p",
};

const RAW_TO_LABEL: Record<string, { key: string; fallback: string }> = {
	default: { key: "account-type.manual.label", fallback: "Manual" },
	disabled: { key: "account-type.manual.label", fallback: "Manual" },
	unifiedAccount: {
		key: "account-type.unified.label",
		fallback: "Unified Account",
	},
	portfolioMargin: {
		key: "account-type.portfolio-margin.label",
		fallback: "Portfolio Margin",
	},
	dexAbstraction: {
		key: "account-type.dex-abstraction.label",
		fallback: "DEX Abstraction",
	},
};

export function AccountTypeDialog({
	triggerClassName,
}: {
	triggerClassName?: string;
}) {
	const { t } = useTranslate();
	const abstraction = useUserAbstraction();
	const setAccountType = useSetAccountAbstraction();

	const currentRaw = abstraction.data;
	const currentValue = currentRaw ? RAW_TO_VALUE[currentRaw] : undefined;
	const currentLabelInfo = currentRaw
		? (RAW_TO_LABEL[currentRaw] ?? RAW_TO_LABEL.default)
		: undefined;
	const currentLabel = currentLabelInfo
		? t(currentLabelInfo.key, currentLabelInfo.fallback)
		: "—";

	const [open, setOpen] = useState(false);
	const [pending, setPending] = useState<AbstractionMode>(currentValue ?? "i");

	function handleOpenChange(nextOpen: boolean) {
		setOpen(nextOpen);
		if (nextOpen) setPending(currentValue ?? "i");
	}

	async function handleConfirm() {
		if (setAccountType.isPending) return;
		if (pending === currentValue) {
			setOpen(false);
			return;
		}
		try {
			await setAccountType.mutateAsync(pending);
			setOpen(false);
		} catch {
			// surfaced via setAccountType.error + toast
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="xs" className={triggerClassName}>
					<SlidersHorizontal className="h-3 w-3" />
					{t("account-type.menu", "Account Type")}
					<span className="ml-auto text-foreground">
						{currentLabel}
					</span>
				</Button>
			</DialogTrigger>
			<DialogContent className="w-[min(400px,calc(100vw-2rem))] rounded-[18px] border border-trade-border-subtle bg-[linear-gradient(180deg,var(--trade-surface-overlay),var(--trade-surface-overlay-end))] p-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04),0_30px_60px_rgba(0,0,0,0.45)]">
				<div className="flex items-center justify-between gap-3">
					<DialogTitle className="text-body2-bold text-trade-text-strong">
						{t("account-type.title", "Account Type")}
					</DialogTitle>
					<DialogCloseButton aria-label="Close account type dialog" />
				</div>

				<p className="mt-1 text-label2 text-trade-text-secondary">
					{t("account-type.current", "Current")}:{" "}
					<span className="text-label2-medium text-trade-text-strong">
						{currentLabel}
					</span>
				</p>

				<div className="mt-3 grid gap-3">
					{ACCOUNT_TYPE_OPTIONS.map((option) => {
						const isSelected = pending === option.value;
						const isCurrent = currentValue === option.value;
						return (
							<button
								key={option.value}
								type="button"
								onClick={() => setPending(option.value)}
								className={cn(
									"flex w-full cursor-pointer flex-col gap-2.5 rounded-[10px] border border-trade-border-subtle bg-trade-surface p-3.5 text-left transition-[border-color,background-color,box-shadow] hover:bg-trade-interactive",
									isSelected &&
										"border-trade-accent bg-[linear-gradient(180deg,color-mix(in_srgb,var(--color-theme-primary-normal)_8%,transparent)_0%,color-mix(in_srgb,var(--color-theme-primary-normal)_3%,transparent)_100%)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-theme-primary-normal)_14%,transparent)]",
								)}
							>
								<div className="flex items-center gap-2.5 text-body2-bold text-trade-text-strong">
									<CheckboxIndicator checked={isSelected} />
									<span>{t(option.labelKey, option.labelFallback)}</span>
									{isCurrent && (
										<span className="ml-auto text-caption1-medium text-trade-text-secondary">
											{t("account-type.current", "Current")}
										</span>
									)}
								</div>
								<p className="text-label2 text-trade-text-secondary">
									{t(option.descKey, option.descFallback)}
								</p>
							</button>
						);
					})}
				</div>

				{setAccountType.error && (
					<p className="mt-2 text-label2 text-trade-red">
						{getHyperliquidErrorMessage(
							t,
							setAccountType.error,
							t("account-type.error", "Failed to update account type"),
						)}
					</p>
				)}

				<Button
					disabled={setAccountType.isPending || pending === currentValue}
					className="mt-3.5 min-h-11 w-full transition-[filter,transform] hover:brightness-105 disabled:opacity-60"
					onClick={handleConfirm}
				>
					{setAccountType.isPending ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" />
							{t("actions.updating")}
						</>
					) : (
						t("actions.confirm")
					)}
				</Button>
			</DialogContent>
		</Dialog>
	);
}
